import { format } from "date-fns/format";

import {
	CreditApplicationStatus,
	OrderEntityType,
	OrderStatus,
	PaymentStatus,
	UserRole,
	UserStatus
} from "~/gql/graphqlBase.generated";
import {
	SET_TEXT_MULTI_FILTER_SET_INDEX,
	SET_TEXT_MULTI_FILTER_TEXT_INDEX
} from "~/helpers/ag-grid/createFilteredColumn";
import { formatOrderMoneyStatusValue } from "~/helpers/creditApplicationStatus";
import { getSetFilterValueName } from "~/helpers/grid/agGridSetFilterValues";
import localizedText from "~/helpers/localizedText";
import { getStatusLabel } from "~/helpers/statusMapping";
import { ADMIN_USERS_GRID_ID } from "~admin/users/helpers/adminUsersSsrmDatasource";
import { formatUserRole, formatUserStatus, withoutRetiredRoles } from "~admin/users/helpers/userRoleDisplay";
import { INVOICES_GRID_ID } from "~fulfill/sales/invoices/helpers/invoicesSsrmRow";
import { HARDGOODS_ITEM_QUERY_PARAM } from "~produce/inventory/hardgoods/helpers/hardgoodsSsrmRow";
import formatJobStepLabel, { JOB_STEP_FILTER_VALUES } from "~produce/production/schedule/helpers/jobStep";
import formatLotStatusFilterLabel, {
	LOT_STATUS_FILTER_VALUES
} from "~produce/production/schedule/helpers/lotStatusFilter";
import { PRODUCTION_SCHEDULE_GRID_ID } from "~produce/production/schedule/helpers/productionScheduleSsrmRow";

export type DeepLinkFilterKind = "date" | "number" | "set" | "text";

export type DeepLinkNumberOperator = "equals" | "greaterThan" | "inRange" | "lessThan";

export interface DeepLinkFieldSpec {
	field: string;
	formatValues: (values: string[]) => string[];
	kind: DeepLinkFilterKind;
	label: string;
	labelKey?: string;
	param: string;
	parse: (raw: string) => string[];
	rendersAsMultiColumnFilter?: boolean;
}

export interface AppliedDeepLinkFilter {
	field: string;
	kind: DeepLinkFilterKind;
	label: string;
	param: string;
	rendersAsMultiColumnFilter?: boolean;
	valueLabels: string[];
	values: string[];
}

export interface DeepLinkSetFilter {
	filterType: "set";
	values: string[];
}

export interface DeepLinkDateFilter {
	dateFrom: string;
	dateTo: string;
	filterType: "date";
	type: "inRange";
}

export interface DeepLinkTextFilter {
	filter: string;
	filterType: "text";
	type: "equals";
}

export interface DeepLinkBlankTextFilter {
	filterType: "text";
	type: "blank";
}

export interface DeepLinkNumberFilter {
	filter: number;
	filterTo?: number;
	filterType: "number";
	type: DeepLinkNumberOperator;
}

export type DeepLinkFilterModelEntry =
	| DeepLinkBlankTextFilter
	| DeepLinkDateFilter
	| DeepLinkNumberFilter
	| DeepLinkSetFilter
	| DeepLinkTextFilter;

export interface DeepLinkMultiFilterModelEntry {
	filterModels: (DeepLinkFilterModelEntry | null)[];
	filterType: "multi";
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidDay = (value: string): boolean =>
	DAY_PATTERN.test(value) && !Number.isNaN(new Date(`${value}T00:00:00`).getTime());

const dayStart = (day: string): string => `${day} 00:00:00`;

const dayEnd = (day: string): string => `${day} 23:59:59`;

const dayOf = (dateTime: string): string => dateTime.slice(0, 10);

const dayLabel = (day: string): string => format(new Date(`${day}T00:00:00`), "MMM d, yyyy");

const enumSetParser =
	(enumObject: Record<string, string>) =>
	(raw: string): string[] => {
		const byUpperValue = new Map(Object.values(enumObject).map((value) => [value.toUpperCase(), value]));
		const parsed: string[] = [];
		for (const token of raw.split(",")) {
			const match = byUpperValue.get(token.trim().toUpperCase());
			if (match && !parsed.includes(match)) parsed.push(match);
		}
		return parsed;
	};

const enumSetFormatter =
	(formatOne: (value: string) => string) =>
	(values: string[]): string[] =>
		values.map((value) => formatOne(value) || value);

const rawSetParser = (raw: string): string[] => {
	const parsed: string[] = [];
	for (const token of raw.split(",")) {
		const trimmed = token.trim();
		if (trimmed && !parsed.includes(trimmed)) parsed.push(trimmed);
	}
	return parsed;
};

const rawSingleValueParser = (raw: string): string[] => {
	const trimmed = raw.trim();
	return trimmed ? [trimmed] : [];
};

const rawSingleOrBlankValueParser = (raw: string): string[] => [raw.trim()];

const identityFormatter = (values: string[]): string[] => values;

const parseDeliveryRange = (raw: string): string[] => {
	const parts = raw.split("..").map((part) => part.trim());
	if (parts.length !== 2) return [];
	if (!isValidDay(parts[0]) || !isValidDay(parts[1])) return [];
	return parts;
};

const formatDeliveryRange = (values: string[]): string[] =>
	values.length === 2 ? [`${dayLabel(values[0])} – ${dayLabel(values[1])}`] : [];

const NUMBER_OPERATOR_PREFIXES: Record<string, DeepLinkNumberOperator> = { gt: "greaterThan", lt: "lessThan" };

const NUMBER_OPERATOR_SYMBOLS: Record<DeepLinkNumberOperator, string> = {
	equals: "=",
	greaterThan: ">",
	inRange: "–",
	lessThan: "<"
};

const isFiniteNumberString = (value: string): boolean => value.trim() !== "" && Number.isFinite(Number(value));

const parseNumberRange = (raw: string): string[] => {
	const parts = raw.split("..").map((part) => part.trim());
	if (parts.length !== 2) return [];
	const [from, to] = parts;
	return isFiniteNumberString(from) && isFiniteNumberString(to) ? ["inRange", from, to] : [];
};

const parseNumberOperator = (raw: string): string[] => {
	const [prefix, value] = raw.split(":");
	const operator = NUMBER_OPERATOR_PREFIXES[prefix?.trim() ?? ""];
	const trimmedValue = value?.trim() ?? "";
	return operator && isFiniteNumberString(trimmedValue) ? [operator, trimmedValue] : [];
};

const parseNumberEquals = (raw: string): string[] => {
	const trimmed = raw.trim();
	return isFiniteNumberString(trimmed) ? ["equals", trimmed] : [];
};

const parseNumberSpec = (raw: string): string[] => {
	if (raw.includes("..")) return parseNumberRange(raw);
	if (raw.includes(":")) return parseNumberOperator(raw);
	return parseNumberEquals(raw);
};

const formatNumberValues = (values: string[]): string[] => {
	const [operator, ...numbers] = values;
	if (operator === "inRange") return [`${numbers[0]} ${NUMBER_OPERATOR_SYMBOLS.inRange} ${numbers[1]}`];
	return [`${NUMBER_OPERATOR_SYMBOLS[operator as DeepLinkNumberOperator]} ${numbers[0]}`];
};

const numberFieldSpec = (field: string, label: string, labelKey: string): DeepLinkFieldSpec => ({
	field,
	formatValues: formatNumberValues,
	kind: "number",
	label,
	labelKey,
	param: field,
	parse: parseNumberSpec
});

const ORDER_ENTITY_TYPE_LABELS: Record<OrderEntityType, string> = {
	[OrderEntityType.MasterOrder]: "Master Order",
	[OrderEntityType.Order]: "Order"
};

const formatOrderEntityType = (value: string): string => ORDER_ENTITY_TYPE_LABELS[value as OrderEntityType] ?? value;

const ORDERS_STATUS_SPEC: DeepLinkFieldSpec = {
	field: "status",
	formatValues: enumSetFormatter((value) => getStatusLabel(value as OrderStatus)),
	kind: "set",
	label: "Status",
	param: "status",
	parse: enumSetParser(OrderStatus)
};

const ORDERS_PAYMENT_STATUS_SPEC: DeepLinkFieldSpec = {
	field: "paymentStatus",
	formatValues: enumSetFormatter(formatOrderMoneyStatusValue),
	kind: "set",
	label: "Payment Status",
	param: "paymentStatus",
	parse: enumSetParser({ ...PaymentStatus, ...CreditApplicationStatus })
};

const ORDERS_ENTITY_TYPE_SPEC: DeepLinkFieldSpec = {
	field: "orderEntityType",
	formatValues: enumSetFormatter(formatOrderEntityType),
	kind: "set",
	label: "Order Kind",
	param: "orderEntityType",
	parse: enumSetParser(OrderEntityType)
};

const ORDERS_DELIVERY_SPEC: DeepLinkFieldSpec = {
	field: "expectedDeliveryDate",
	formatValues: formatDeliveryRange,
	kind: "date",
	label: "Expected Delivery",
	param: "expectedDeliveryDate",
	parse: parseDeliveryRange
};

const ORDERS_INITIALIZED_DATE_SPEC: DeepLinkFieldSpec = {
	field: "initializedDate",
	formatValues: formatDeliveryRange,
	kind: "date",
	label: "Booked",
	labelKey: "deepLink.orders.booked",
	param: "initializedDate",
	parse: parseDeliveryRange
};

const ORDERS_SHIPPED_DATE_SPEC: DeepLinkFieldSpec = {
	field: "shippedDate",
	formatValues: formatDeliveryRange,
	kind: "date",
	label: "Shipped",
	labelKey: "deepLink.orders.shipped",
	param: "shippedDate",
	parse: parseDeliveryRange
};

const ORDERS_CUSTOMER_NAME_SPEC: DeepLinkFieldSpec = {
	field: "customerName",
	formatValues: identityFormatter,
	kind: "text",
	label: "Customer",
	param: "customerName",
	parse: rawSingleValueParser,
	rendersAsMultiColumnFilter: true
};

const ORDERS_SALES_PERSON_SPEC: DeepLinkFieldSpec = {
	field: "salesPerson",
	formatValues: identityFormatter,
	kind: "text",
	label: "Salesperson",
	labelKey: "deepLink.orders.salesperson",
	param: "salesPerson",
	parse: rawSingleValueParser,
	rendersAsMultiColumnFilter: true
};

const ORDERS_SHIP_FROM_SITE_SPEC: DeepLinkFieldSpec = {
	field: "shipFromSiteName",
	formatValues: identityFormatter,
	kind: "set",
	label: "Ship-From Site",
	labelKey: "deepLink.orders.shipFromSite",
	param: "shipFromSiteName",
	parse: rawSingleValueParser,
	rendersAsMultiColumnFilter: true
};

const ADMIN_USERS_ROLES_SPEC: DeepLinkFieldSpec = {
	field: "roles",
	formatValues: enumSetFormatter((value) => formatUserRole(value as UserRole)),
	kind: "set",
	label: "Roles",
	param: "roles",
	parse: (raw) => withoutRetiredRoles(enumSetParser(UserRole)(raw))
};

const ADMIN_USERS_STATUS_SPEC: DeepLinkFieldSpec = {
	field: "status",
	formatValues: enumSetFormatter((value) => formatUserStatus(value as UserStatus)),
	kind: "set",
	label: "Status",
	param: "status",
	parse: enumSetParser(UserStatus)
};

const formatAdminUserSiteIdWithColdCacheFallback = (id: string): string =>
	getSetFilterValueName(ADMIN_USERS_GRID_ID, "assignedSites", id) ?? id;

const ADMIN_USERS_SITES_SPEC: DeepLinkFieldSpec = {
	field: "assignedSites",
	formatValues: (values) => values.map(formatAdminUserSiteIdWithColdCacheFallback),
	kind: "set",
	label: "Sites",
	param: "assignedSites",
	parse: rawSetParser
};

const PRODUCTION_SCHEDULE_LOT_SPEC: DeepLinkFieldSpec = {
	field: "ag-Grid-AutoColumn",
	formatValues: identityFormatter,
	kind: "text",
	label: "Lot",
	labelKey: "deepLink.productionSchedule.lot",
	param: "lotCode",
	parse: rawSingleValueParser
};

const PRODUCTION_SCHEDULE_START_WEEK_SPEC = numberFieldSpec(
	"weekStartIsoWeek",
	"Start Week",
	"deepLink.productionSchedule.startWeek"
);

const PRODUCTION_SCHEDULE_READY_WEEK_SPEC = numberFieldSpec(
	"weekReadyIsoWeek",
	"Ready Week",
	"deepLink.productionSchedule.readyWeek"
);

const PRODUCTION_SCHEDULE_READY_YEAR_SPEC = numberFieldSpec(
	"weekReadyIsoYear",
	"Ready Year",
	"deepLink.productionSchedule.readyYear"
);

const toValueRecord = (values: readonly string[]): Record<string, string> =>
	Object.fromEntries(values.map((value) => [value, value]));

const PRODUCTION_SCHEDULE_STEP_SPEC: DeepLinkFieldSpec = {
	field: "step",
	formatValues: enumSetFormatter(formatJobStepLabel),
	kind: "set",
	label: "Step",
	labelKey: "deepLink.productionSchedule.step",
	param: "step",
	parse: enumSetParser(toValueRecord(JOB_STEP_FILTER_VALUES))
};

const PRODUCTION_SCHEDULE_LOT_STATUS_SPEC: DeepLinkFieldSpec = {
	field: "lotStatus",
	formatValues: enumSetFormatter(formatLotStatusFilterLabel),
	kind: "set",
	label: "Lot Status",
	labelKey: "deepLink.productionSchedule.lotStatus",
	param: "lotStatus",
	parse: enumSetParser(toValueRecord(LOT_STATUS_FILTER_VALUES))
};

const PRODUCTION_SCHEDULE_CATEGORY_SPEC: DeepLinkFieldSpec = {
	field: "categoryName",
	formatValues: identityFormatter,
	kind: "text",
	label: "Category",
	labelKey: "deepLink.productionSchedule.category",
	param: "category",
	parse: rawSingleOrBlankValueParser,
	rendersAsMultiColumnFilter: true
};

const PRODUCTION_SCHEDULE_GENUS_SPEC: DeepLinkFieldSpec = {
	field: "genus",
	formatValues: identityFormatter,
	kind: "text",
	label: "Genus",
	labelKey: "deepLink.productionSchedule.genus",
	param: "genus",
	parse: rawSingleOrBlankValueParser,
	rendersAsMultiColumnFilter: true
};

const PRODUCTION_SCHEDULE_SERIES_SPEC: DeepLinkFieldSpec = {
	field: "series",
	formatValues: identityFormatter,
	kind: "text",
	label: "Series",
	labelKey: "deepLink.productionSchedule.series",
	param: "series",
	parse: rawSingleOrBlankValueParser,
	rendersAsMultiColumnFilter: true
};

const PRODUCTION_SCHEDULE_COLOR_SPEC: DeepLinkFieldSpec = {
	field: "color",
	formatValues: identityFormatter,
	kind: "text",
	label: "Color",
	labelKey: "deepLink.productionSchedule.color",
	param: "color",
	parse: rawSingleOrBlankValueParser,
	rendersAsMultiColumnFilter: true
};

const formatScheduleOrderIdWithColdCacheFallback = (id: string): string =>
	getSetFilterValueName(PRODUCTION_SCHEDULE_GRID_ID, "orderIds", id) ?? id;

const PRODUCTION_SCHEDULE_ORDER_SPEC: DeepLinkFieldSpec = {
	field: "orderIds",
	formatValues: (values) => values.map(formatScheduleOrderIdWithColdCacheFallback),
	kind: "set",
	label: "Order",
	labelKey: "deepLink.productionSchedule.order",
	param: "orderId",
	parse: rawSetParser
};

const formatSchedulePlanIdWithColdCacheFallback = (id: string): string =>
	getSetFilterValueName(PRODUCTION_SCHEDULE_GRID_ID, "productionPlanName", id) ?? id;

const PRODUCTION_SCHEDULE_PLAN_SPEC: DeepLinkFieldSpec = {
	field: "productionPlanName",
	formatValues: (values) => values.map(formatSchedulePlanIdWithColdCacheFallback),
	kind: "set",
	label: "Plan",
	labelKey: "deepLink.productionSchedule.plan",
	param: "productionPlanId",
	parse: rawSingleValueParser,
	rendersAsMultiColumnFilter: true
};

const HARDGOODS_ITEM_SPEC: DeepLinkFieldSpec = {
	field: "itemName",
	formatValues: identityFormatter,
	kind: "text",
	label: "Item",
	labelKey: "deepLink.hardgoods.item",
	param: HARDGOODS_ITEM_QUERY_PARAM,
	parse: rawSingleValueParser
};

const RECEIVABLES_AGING_AR_BALANCE_SPEC = numberFieldSpec(
	"arBalance",
	"A/R Balance",
	"deepLink.receivablesAging.arBalance"
);

const RECEIVABLES_AGING_CURRENT_SPEC = numberFieldSpec("current", "Current", "deepLink.receivablesAging.current");

const RECEIVABLES_AGING_DAYS_1_TO_30_SPEC = numberFieldSpec("days1To30", "1-30", "deepLink.receivablesAging.days1To30");

const RECEIVABLES_AGING_DAYS_31_TO_60_SPEC = numberFieldSpec(
	"days31To60",
	"31-60",
	"deepLink.receivablesAging.days31To60"
);

const RECEIVABLES_AGING_DAYS_61_TO_90_SPEC = numberFieldSpec(
	"days61To90",
	"61-90",
	"deepLink.receivablesAging.days61To90"
);

const RECEIVABLES_AGING_DAYS_90_PLUS_SPEC = numberFieldSpec(
	"days90Plus",
	"90+",
	"deepLink.receivablesAging.days90Plus"
);

const RECEIVABLES_AGING_ACCRUED_LATE_CHARGES_SPEC = numberFieldSpec(
	"accruedLateCharges",
	"Accrued Late Charges",
	"deepLink.receivablesAging.accruedLateCharges"
);

const RECEIVABLES_AGING_OLDEST_DUE_DATE_SPEC: DeepLinkFieldSpec = {
	field: "oldestDueDate",
	formatValues: formatDeliveryRange,
	kind: "date",
	label: "Oldest Due",
	labelKey: "deepLink.receivablesAging.oldestDueDate",
	param: "oldestDueDate",
	parse: parseDeliveryRange
};

const RECEIVABLES_AGING_CUSTOMER_NAME_SPEC: DeepLinkFieldSpec = {
	field: "customerName",
	formatValues: identityFormatter,
	kind: "text",
	label: "Customer",
	labelKey: "deepLink.receivablesAging.customerName",
	param: "customerName",
	parse: rawSingleValueParser
};

const formatInvoiceCustomerIdWithColdCacheFallback = (id: string): string =>
	getSetFilterValueName(INVOICES_GRID_ID, "customerId", id) ?? id;

const RECEIVE_PAYMENTS_CUSTOMER_SPEC: DeepLinkFieldSpec = {
	field: "customerId",
	formatValues: (values) => values.map(formatInvoiceCustomerIdWithColdCacheFallback),
	kind: "set",
	label: "Customer",
	labelKey: "deepLink.receivePayments.customer",
	param: "customerId",
	parse: rawSetParser
};

const REGISTRY: Record<string, DeepLinkFieldSpec[]> = {
	"/fulfill/orders": [
		ORDERS_STATUS_SPEC,
		ORDERS_PAYMENT_STATUS_SPEC,
		ORDERS_ENTITY_TYPE_SPEC,
		ORDERS_DELIVERY_SPEC,
		ORDERS_INITIALIZED_DATE_SPEC,
		ORDERS_SHIPPED_DATE_SPEC,
		ORDERS_CUSTOMER_NAME_SPEC,
		ORDERS_SALES_PERSON_SPEC,
		ORDERS_SHIP_FROM_SITE_SPEC
	],
	"/fulfill/sales/invoices": [RECEIVE_PAYMENTS_CUSTOMER_SPEC],
	"/fulfill/sales/receivables": [RECEIVE_PAYMENTS_CUSTOMER_SPEC],
	"/fulfill/sales/receivables-aging": [
		RECEIVABLES_AGING_AR_BALANCE_SPEC,
		RECEIVABLES_AGING_CURRENT_SPEC,
		RECEIVABLES_AGING_DAYS_1_TO_30_SPEC,
		RECEIVABLES_AGING_DAYS_31_TO_60_SPEC,
		RECEIVABLES_AGING_DAYS_61_TO_90_SPEC,
		RECEIVABLES_AGING_DAYS_90_PLUS_SPEC,
		RECEIVABLES_AGING_ACCRUED_LATE_CHARGES_SPEC,
		RECEIVABLES_AGING_OLDEST_DUE_DATE_SPEC,
		RECEIVABLES_AGING_CUSTOMER_NAME_SPEC
	],
	"/produce/inventory/hardgoods": [HARDGOODS_ITEM_SPEC],
	"/produce/production/schedule": [
		PRODUCTION_SCHEDULE_LOT_SPEC,
		PRODUCTION_SCHEDULE_START_WEEK_SPEC,
		PRODUCTION_SCHEDULE_READY_WEEK_SPEC,
		PRODUCTION_SCHEDULE_READY_YEAR_SPEC,
		PRODUCTION_SCHEDULE_STEP_SPEC,
		PRODUCTION_SCHEDULE_LOT_STATUS_SPEC,
		PRODUCTION_SCHEDULE_CATEGORY_SPEC,
		PRODUCTION_SCHEDULE_GENUS_SPEC,
		PRODUCTION_SCHEDULE_SERIES_SPEC,
		PRODUCTION_SCHEDULE_COLOR_SPEC,
		PRODUCTION_SCHEDULE_ORDER_SPEC,
		PRODUCTION_SCHEDULE_PLAN_SPEC
	],
	"/settings/users": [ADMIN_USERS_ROLES_SPEC, ADMIN_USERS_SITES_SPEC, ADMIN_USERS_STATUS_SPEC]
};

const normalizePathname = (pathname: string): string => {
	const trimmed = pathname.trim();
	return trimmed.length > 1 && trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
};

const toApplied = (spec: DeepLinkFieldSpec, values: string[]): AppliedDeepLinkFilter => ({
	field: spec.field,
	kind: spec.kind,
	label: localizedText(spec.label, spec.labelKey),
	param: spec.param,
	rendersAsMultiColumnFilter: spec.rendersAsMultiColumnFilter,
	valueLabels: spec.formatValues(values),
	values
});

const extractSetValues = (entry: unknown): string[] => {
	if (!entry || typeof entry !== "object") return [];
	const candidate = entry as { filterType?: unknown; values?: unknown };
	if (candidate.filterType !== "set" || !Array.isArray(candidate.values)) return [];
	return candidate.values.filter((value): value is string => typeof value === "string");
};

const extractDateValues = (entry: unknown): string[] => {
	if (!entry || typeof entry !== "object") return [];
	const candidate = entry as { dateFrom?: unknown; dateTo?: unknown; filterType?: unknown; type?: unknown };
	if (candidate.filterType !== "date" || candidate.type !== "inRange") return [];
	if (typeof candidate.dateFrom !== "string" || typeof candidate.dateTo !== "string") return [];
	return [dayOf(candidate.dateFrom), dayOf(candidate.dateTo)];
};

const NUMBER_OPERATORS: DeepLinkNumberOperator[] = ["equals", "greaterThan", "inRange", "lessThan"];

const isNumberOperator = (value: unknown): value is DeepLinkNumberOperator =>
	typeof value === "string" && (NUMBER_OPERATORS as string[]).includes(value);

const extractNumberValues = (entry: unknown): string[] => {
	if (!entry || typeof entry !== "object") return [];
	const candidate = entry as { filter?: unknown; filterTo?: unknown; filterType?: unknown; type?: unknown };
	if (candidate.filterType !== "number" || typeof candidate.filter !== "number" || !isNumberOperator(candidate.type)) {
		return [];
	}
	if (candidate.type === "inRange") {
		return typeof candidate.filterTo === "number"
			? [candidate.type, String(candidate.filter), String(candidate.filterTo)]
			: [];
	}
	return [candidate.type, String(candidate.filter)];
};

const extractTextValue = (entry: unknown): string[] => {
	if (!entry || typeof entry !== "object") return [];
	const candidate = entry as { filter?: unknown; filterType?: unknown; type?: unknown };
	if (candidate.filterType !== "text" || candidate.type !== "equals") return [];
	if (typeof candidate.filter !== "string" || candidate.filter.trim() === "") return [];
	return [candidate.filter];
};

const unwrapMultiColumnFilterEntry = (entry: unknown, kind: DeepLinkFilterKind): unknown => {
	if (!entry || typeof entry !== "object") return entry;
	const candidate = entry as { filterModels?: unknown; filterType?: unknown };
	if (candidate.filterType !== "multi" || !Array.isArray(candidate.filterModels)) return entry;
	return candidate.filterModels.find(
		(sub) => sub && typeof sub === "object" && (sub as { filterType?: unknown }).filterType === kind
	);
};

const readSpecValues = (spec: DeepLinkFieldSpec, entry: unknown): string[] => {
	const resolved = spec.rendersAsMultiColumnFilter ? unwrapMultiColumnFilterEntry(entry, spec.kind) : entry;
	if (spec.kind === "date") return extractDateValues(resolved);
	if (spec.kind === "number") return extractNumberValues(resolved);
	if (spec.kind === "text") return extractTextValue(resolved);
	return extractSetValues(resolved);
};

export const getDeepLinkFieldSpecs = (pathname: string): DeepLinkFieldSpec[] =>
	REGISTRY[normalizePathname(pathname)] ?? [];

export const consumedDeepLinkParams = (pathname: string): string[] =>
	getDeepLinkFieldSpecs(pathname).map((spec) => spec.param);

export const resolveDeepLinkFilters = (pathname: string, search: URLSearchParams): AppliedDeepLinkFilter[] => {
	const applied: AppliedDeepLinkFilter[] = [];
	for (const spec of getDeepLinkFieldSpecs(pathname)) {
		const raw = search.get(spec.param);
		if (raw === null) continue;
		const values = spec.parse(raw);
		if (values.length > 0) applied.push(toApplied(spec, values));
	}
	return applied;
};

export const resolveDeepLinkFiltersFromPath = (path: string): AppliedDeepLinkFilter[] => {
	const withoutHash = path.split("#")[0];
	const queryIndex = withoutHash.indexOf("?");
	const pathname = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
	const search = new URLSearchParams(queryIndex === -1 ? "" : withoutHash.slice(queryIndex + 1));
	return resolveDeepLinkFilters(pathname, search);
};

export const readActiveDeepLinkFilters = (
	pathname: string,
	filterModel: Record<string, unknown> | null | undefined
): AppliedDeepLinkFilter[] => {
	if (!filterModel) return [];
	const applied: AppliedDeepLinkFilter[] = [];
	for (const spec of getDeepLinkFieldSpecs(pathname)) {
		const values = readSpecValues(spec, filterModel[spec.field]);
		if (values.length > 0) applied.push(toApplied(spec, values));
	}
	return applied;
};

const buildDeepLinkFilterModelEntry = (filter: AppliedDeepLinkFilter): DeepLinkFilterModelEntry => {
	if (filter.kind === "date") {
		return {
			dateFrom: dayStart(filter.values[0]),
			dateTo: dayEnd(filter.values[1]),
			filterType: "date",
			type: "inRange"
		};
	}
	if (filter.kind === "text") {
		return filter.values[0] === ""
			? { filterType: "text", type: "blank" }
			: { filter: filter.values[0], filterType: "text", type: "equals" };
	}
	if (filter.kind === "number") {
		const [operator, ...numbers] = filter.values;
		const numberOperator = operator as DeepLinkNumberOperator;
		return numbers.length === 2
			? { filter: Number(numbers[0]), filterTo: Number(numbers[1]), filterType: "number", type: numberOperator }
			: { filter: Number(numbers[0]), filterType: "number", type: numberOperator };
	}
	return { filterType: "set", values: filter.values };
};

const wrapAsMultiColumnFilterEntry = (
	filter: AppliedDeepLinkFilter,
	entry: DeepLinkFilterModelEntry
): DeepLinkMultiFilterModelEntry => {
	const filterModels: (DeepLinkFilterModelEntry | null)[] = [null, null];
	filterModels[filter.kind === "text" ? SET_TEXT_MULTI_FILTER_TEXT_INDEX : SET_TEXT_MULTI_FILTER_SET_INDEX] = entry;
	return { filterModels, filterType: "multi" };
};

export const buildDeepLinkFilterModel = (
	applied: AppliedDeepLinkFilter[]
): Record<string, DeepLinkFilterModelEntry | DeepLinkMultiFilterModelEntry> => {
	const model: Record<string, DeepLinkFilterModelEntry | DeepLinkMultiFilterModelEntry> = {};
	for (const filter of applied) {
		const entry = buildDeepLinkFilterModelEntry(filter);
		model[filter.field] = filter.rendersAsMultiColumnFilter ? wrapAsMultiColumnFilterEntry(filter, entry) : entry;
	}
	return model;
};
